import { Component, computed, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { updateParamsObjectToPageOne } from '../../../utils/filter-utils';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { SelectOption } from '../../../../_interfaces/select-field.interface';
import { FocusTrapModule } from 'ngx-bootstrap/focus-trap';
import { toSignal } from '@angular/core/rxjs-interop';
import { ResponsiveService } from '../../../../_services/responsive-service/responsive.service';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { SearchParamKeys } from './search-utils';

@Component({
  selector: 'app-search-field',
  imports: [
    FormsModule,
    NgSelectModule,
    FocusTrapModule,
    NgClass,
    NgTemplateOutlet,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
  ],
  templateUrl: './search-field.component.html',
  styleUrl: '../../../../../static/styles/components/search-field.component.scss',
})
export class SearchFieldComponent implements OnInit {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly responsiveService = inject(ResponsiveService);

  @Input() value: string = '';
  @Input() searchOptions: Array<SelectOption> = [];
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  queryParams = new HttpParams();
  selectedSearch: string | undefined;
  selectedSearchLabel: string | undefined;

  protected readonly isMobile = this.responsiveService.isMobile;

  constructor() {
    this.queryParams = new HttpParams().appendAll(this.route.snapshot.queryParams);
  }

  ngOnInit(): void {
    if (this.searchOptions && this.searchOptions?.length == 1) {
      this.selectedSearch = this.searchOptions[0]?.value;
      this.onSelectOption();
    }
  }

  private readonly currentParams = toSignal(this.route.queryParams, { initialValue: {} });

  // Derive the indicator state
  public readonly hasActiveFilters = computed(() => {
    const params = this.currentParams();
    const searchParamValues = Object.values(SearchParamKeys);
    const filterKeys = Object.keys(params).filter((key) => searchParamValues.includes(key as SearchParamKeys));
    return filterKeys.length > 0;
  });

  onSelectOption() {
    this.selectedSearchLabel = this.searchOptions.find((opt) => opt.value === this.selectedSearch)?.option;
  }

  onSearch() {
    // create "clean" query params, otherwise historical queries gets added again.
    const existingParams = this.route.snapshot.queryParams;
    this.queryParams = new HttpParams().appendAll(existingParams);

    // add new queries
    if (this.value && this.selectedSearch) {
      this.queryParams = this.queryParams.set(this.selectedSearch, this.value);
    }

    // Navigate with updated filters and reset page to 1
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObjectToPageOne(this.queryParams),
    });

    // Clear the input value
    this.value = '';

    // Only clear selection if there are multiple search options
    // If there's only 1 option, keep it selected for convenience
    if (this.searchOptions.length > 1) {
      this.selectedSearch = undefined;
      this.selectedSearchLabel = '';
    }
  }

  onDelete(param: string) {
    // create "clean" query params, otherwise historical queries gets added again.
    const existingParams = this.route.snapshot.queryParams;
    this.queryParams = new HttpParams().appendAll(existingParams);

    // delete selected query and reset page to 1
    this.queryParams = this.queryParams.delete(param);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObjectToPageOne(this.queryParams),
    });
  }
}
